import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Match, RivalryStats } from '../types';
import {
  getMatchHistory,
  getRivalryStats,
  requestRematch,
} from '../services/matchmaking.service';

const Container = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing.md};

  @media (max-width: 767px) {
    padding: ${({ theme }) => theme.spacing.sm};
    padding-top: ${({ theme }) => theme.spacing.md};
  }
`;

const Header = styled.div`
  max-width: 1000px;
  margin: 0 auto ${({ theme }) => theme.spacing.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Title = styled.h1`
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;

  @media (max-width: 767px) {
    font-size: 1.5rem;
  }
`;

const BackButton = styled(motion.button)`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  font-size: 1rem;
  font-weight: 600;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 2px solid ${({ theme }) => theme.colors.primary};
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: #fff;
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 767px) {
    font-size: 0.9rem;
    padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  }
`;

const Content = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const MatchList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const MatchCard = styled(motion.div)<{ $isWin: boolean; $isDraw: boolean }>`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  box-shadow: ${({ theme }) => theme.shadows.md};
  border-left: 4px solid
    ${({ theme, $isWin, $isDraw }) =>
      $isDraw ? theme.colors.warning : $isWin ? theme.colors.success : theme.colors.error};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }

  transition: all 0.2s ease;

  @media (max-width: 767px) {
    padding: ${({ theme }) => theme.spacing.sm};
    gap: ${({ theme }) => theme.spacing.xs};
  }
`;

const MatchInfo = styled.div`
  flex: 1;
  min-width: 250px;
`;

const MatchHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const ResultBadge = styled.span<{ $type: 'win' | 'loss' | 'draw' }>`
  display: inline-block;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background: ${({ theme, $type }) =>
    $type === 'draw'
      ? theme.colors.warning
      : $type === 'win'
      ? theme.colors.success
      : theme.colors.error};
  color: white;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-weight: 700;
  font-size: 0.85rem;
  text-transform: uppercase;
`;

const OpponentName = styled.span`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const ScoreDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const YourScore = styled.span<{ $isWin: boolean }>`
  font-weight: 700;
  color: ${({ theme, $isWin }) => ($isWin ? theme.colors.success : theme.colors.text)};
`;

const OpponentScore = styled.span<{ $isLoss: boolean }>`
  font-weight: 700;
  color: ${({ theme, $isLoss }) => ($isLoss ? theme.colors.error : theme.colors.text)};
`;

const MatchDate = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const RivalryButton = styled(motion.button)`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  font-size: 0.8rem;
  font-weight: 600;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 2px solid ${({ theme }) => theme.colors.secondary};
  background: transparent;
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.secondary};
    color: #fff;
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 767px) {
    font-size: 0.75rem;
    padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.xs};
  }
`;

const RematchButton = styled(motion.button)`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: none;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${({ theme }) => theme.shadows.sm};
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 767px) {
    font-size: 0.8rem;
    padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1.1rem;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1.1rem;
`;

const RivalryModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${({ theme }) => theme.spacing.md};
`;

const RivalryCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  max-width: 500px;
  width: 100%;
  box-shadow: ${({ theme }) => theme.shadows.lg};
`;

const RivalryTitle = styled.h2`
  font-size: 1.8rem;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  text-align: center;
`;

const RivalryStatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: ${({ theme }) => theme.spacing.md};
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const StatBox = styled.div`
  text-align: center;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.secondary};
`;

const VSText = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
`;

const CloseButton = styled(motion.button)`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  font-size: 1rem;
  font-weight: 600;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: none;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const MatchHistoryScreen: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser: user } = useAuth();
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRivalry, setSelectedRivalry] = useState<{
    opponentId: string;
    opponentName: string;
    stats: RivalryStats;
  } | null>(null);
  const [requestingRematch, setRequestingRematch] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadMatchHistory();
    }
  }, [user]);

  const loadMatchHistory = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const history = await getMatchHistory(user.uid, 20);
      setMatches(history);
    } catch (error) {
      console.error('Failed to load match history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewRivalry = async (opponentId: string, opponentName: string) => {
    if (!user) return;

    try {
      const stats = await getRivalryStats(user.uid, opponentId);
      setSelectedRivalry({ opponentId, opponentName, stats });
    } catch (error) {
      console.error('Failed to load rivalry stats:', error);
    }
  };

  const handleRequestRematch = async (matchId: string) => {
    if (!user) return;

    try {
      setRequestingRematch(matchId);
      await requestRematch(matchId, user.uid);
      alert('Rematch requested! Your opponent will be notified.');
    } catch (error) {
      console.error('Failed to request rematch:', error);
      alert('Failed to request rematch. Please try again.');
    } finally {
      setRequestingRematch(null);
    }
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return 'Unknown date';

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  if (loading) {
    return (
      <Container>
        <Header>
          <Title>Match History</Title>
          <BackButton onClick={() => navigate(-1)} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            Back
          </BackButton>
        </Header>
        <Content>
          <LoadingState>Loading your match history...</LoadingState>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Match History</Title>
        <BackButton onClick={() => navigate(-1)} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          Back
        </BackButton>
      </Header>

      <Content>
        {matches.length === 0 ? (
          <EmptyState>
            No matches yet. Play your first multiplayer game to see your history here!
          </EmptyState>
        ) : (
          <MatchList>
            {matches.map((match, index) => {
              const isPlayer1 = match.player1.uid === user.uid;
              const opponent = isPlayer1 ? match.player2 : match.player1;
              const yourScore = isPlayer1 ? match.player1.score : match.player2?.score || 0;
              const opponentScore = isPlayer1 ? match.player2?.score || 0 : match.player1.score;
              const isWin = match.winnerId === user.uid;
              const isDraw = match.winnerId === null;
              const isLoss = !isWin && !isDraw;

              return (
                <MatchCard
                  key={match.id}
                  $isWin={isWin}
                  $isDraw={isDraw}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <MatchInfo>
                    <MatchHeader>
                      <ResultBadge $type={isDraw ? 'draw' : isWin ? 'win' : 'loss'}>
                        {isDraw ? 'Draw' : isWin ? 'Win' : 'Loss'}
                      </ResultBadge>
                      <OpponentName>vs {opponent?.displayName || 'Unknown'}</OpponentName>
                    </MatchHeader>
                    <ScoreDisplay>
                      <YourScore $isWin={isWin}>You: {yourScore}</YourScore>
                      <span>-</span>
                      <OpponentScore $isLoss={isLoss}>
                        {opponent?.displayName || 'Opponent'}: {opponentScore}
                      </OpponentScore>
                    </ScoreDisplay>
                    <MatchDate>{formatDate(match.completedAt)}</MatchDate>
                  </MatchInfo>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {opponent && (
                      <RivalryButton
                        onClick={() => handleViewRivalry(opponent.uid, opponent.displayName)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        View Rivalry
                      </RivalryButton>
                    )}
                    <RematchButton
                      onClick={() => handleRequestRematch(match.id)}
                      disabled={requestingRematch === match.id || !!match.rematchRequestedBy}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {match.rematchRequestedBy
                        ? 'Rematch Pending'
                        : requestingRematch === match.id
                        ? 'Requesting...'
                        : 'Rematch'}
                    </RematchButton>
                  </div>
                </MatchCard>
              );
            })}
          </MatchList>
        )}
      </Content>

      {selectedRivalry && (
        <RivalryModal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setSelectedRivalry(null)}
        >
          <RivalryCard
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <RivalryTitle>Rivalry Stats</RivalryTitle>
            <OpponentName style={{ display: 'block', textAlign: 'center', marginBottom: '24px' }}>
              You vs {selectedRivalry.opponentName}
            </OpponentName>
            <RivalryStatsGrid>
              <StatBox>
                <StatLabel>Your Wins</StatLabel>
                <StatValue>{selectedRivalry.stats.player1Wins}</StatValue>
              </StatBox>
              <VSText>-</VSText>
              <StatBox>
                <StatLabel>Their Wins</StatLabel>
                <StatValue>{selectedRivalry.stats.player2Wins}</StatValue>
              </StatBox>
            </RivalryStatsGrid>
            {selectedRivalry.stats.draws > 0 && (
              <StatBox style={{ marginBottom: '24px' }}>
                <StatLabel>Draws</StatLabel>
                <StatValue>{selectedRivalry.stats.draws}</StatValue>
              </StatBox>
            )}
            <StatBox style={{ marginBottom: '24px' }}>
              <StatLabel>Total Games</StatLabel>
              <StatValue>{selectedRivalry.stats.totalGames}</StatValue>
            </StatBox>
            <CloseButton
              onClick={() => setSelectedRivalry(null)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Close
            </CloseButton>
          </RivalryCard>
        </RivalryModal>
      )}
    </Container>
  );
};

export default MatchHistoryScreen;
